// @greenlight-spec: 32-knowledge.md
// @greenlight-spec: 28-it-dashboard-ui.md
import * as React from 'react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { canMutateScope, formatKnowledgeDate } from '@/components/knowledge/util';
import { GLDrawer } from '@/components/greenlight/GLDrawer';
import { KnowledgeAssets } from '@/components/knowledge/KnowledgeAssets';
import { MarkdownView } from '@/components/knowledge/MarkdownView';
import { ScopeTile } from '@/components/knowledge/ScopeTile';
import { useScopeResolvers } from '@/components/knowledge/useScopeResolvers';
import { Tag } from '@/components/greenlight/Tag';
import type { KnowledgeEntry } from '@greenlight/contract';

export interface KnowledgeEntryEditorProps {
  /** Entry to edit, or null when the editor is closed. */
  entryId: string | null;
  userRole: string | undefined;
  onClose: () => void;
}

type Visibility = KnowledgeEntry['visibility'];

const VISIBILITY_OPTIONS: { v: Visibility; label: string }[] = [
  { v: 'agent', label: 'Agents only' },
  { v: 'dashboard_only', label: 'Dashboard only' },
  { v: 'both', label: 'Agents + dashboard' },
];

/**
 * The Markdown entry editor with edit history — reused by /knowledge (org & app
 * scopes) and /integrations (integration-scope authoring on the integration it
 * describes, per docs/28 § /knowledge → integration pointer).
 */
export function KnowledgeEntryEditor({
  entryId,
  userRole,
  onClose,
}: KnowledgeEntryEditorProps): React.JSX.Element {
  const entryQueryKey = ['knowledge-entry', entryId];
  const { data } = api.knowledge.getEntry.useQuery(
    entryQueryKey,
    { params: { id: entryId ?? '' } },
    { queryKey: entryQueryKey, enabled: entryId != null },
  );
  const updateMutation = api.knowledge.updateEntry.useMutation();
  const deleteMutation = api.knowledge.deleteEntry.useMutation();
  const historyQueryKey = ['knowledge-entry-history', entryId];
  const { data: historyData } = api.knowledge.listHistory.useQuery(
    historyQueryKey,
    { params: { id: entryId ?? '' } },
    { queryKey: historyQueryKey, enabled: entryId != null },
  );
  // Same query key the Assets section uses, so this shares its cache entry
  // rather than issuing a second request. Preview needs them to resolve the
  // body's `gl-asset:` references (docs/28 §Assets).
  const assetsQueryKey = ['knowledge-assets', entryId];
  const { data: assetsData, isError: assetsErrored } = api.knowledge.listAssets.useQuery(
    assetsQueryKey,
    { params: { id: entryId ?? '' } },
    { queryKey: assetsQueryKey, enabled: entryId != null },
  );
  // Three outcomes, not two. ts-rest resolves a 4xx/5xx into `data`, so a failed
  // read has to be told apart from one that simply has not arrived: undefined
  // means Preview says "resolving", and a failure says so instead of implying
  // every reference is dangling.
  const assetsUnavailable =
    assetsErrored || (assetsData !== undefined && assetsData.status !== 200);
  const previewAssets = assetsData?.status === 200 ? assetsData.body.items : undefined;

  const entry = data?.status === 200 ? data.body : null;
  const history = historyData?.body.items ?? [];

  const [tab, setTab] = React.useState<'edit' | 'history'>('edit');
  const [bodyMode, setBodyMode] = React.useState<'write' | 'preview'>('write');
  const [title, setTitle] = React.useState('');
  const [bodyMd, setBodyMd] = React.useState('');
  const [visibility, setVisibility] = React.useState<Visibility>('both');
  const [tags, setTags] = React.useState<string[]>([]);
  const [newTag, setNewTag] = React.useState('');
  const [confirmDelete, setConfirmDelete] = React.useState(false);

  React.useEffect(() => {
    if (entry) {
      setTitle(entry.title);
      setBodyMd(entry.bodyMd);
      setVisibility(entry.visibility);
      setTags(entry.tags);
      setNewTag('');
      setTab('edit');
      setBodyMode('write');
      setConfirmDelete(false);
    }
  }, [entry?.id, entry]);

  const canEdit = entry ? canMutateScope(userRole, entry.scope) : false;
  const effectiveBodyMode = canEdit ? bodyMode : 'preview';
  const queryClient = useQueryClient();

  const resolve = useScopeResolvers();
  const display =
    entry != null
      ? resolve({ scope: entry.scope, integrationId: entry.integrationId, appId: entry.appId })
      : null;

  const trimmedBody = bodyMd.trim();
  const wordCount = trimmedBody ? trimmedBody.split(/\s+/).length : 0;

  async function onSave(): Promise<void> {
    if (!entry) return;
    const result = await updateMutation.mutateAsync({
      params: { id: entry.id },
      body: { title, bodyMd, visibility, tags },
    });
    if (result.status === 200) {
      queryClient.invalidateQueries({ queryKey: ['knowledge-entries'] });
      queryClient.invalidateQueries({ queryKey: ['knowledge-entry', entry.id] });
      queryClient.invalidateQueries({ queryKey: ['knowledge-entry-history', entry.id] });
      toast.success(`Saved v${result.body.version}.`);
      onClose();
    } else {
      toast.error('Save failed.');
    }
  }

  async function onDelete(): Promise<void> {
    if (!entry) return;
    try {
      const result = await deleteMutation.mutateAsync({ params: { id: entry.id } });
      if (result.status >= 300) throw result;
      queryClient.invalidateQueries({ queryKey: ['knowledge-entries'] });
      toast.success('Knowledge entry deleted.');
      setConfirmDelete(false);
      onClose();
    } catch {
      toast.error('Delete failed.');
    }
  }

  function handleTagKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const trimmed = newTag.trim();
    if (trimmed && !tags.includes(trimmed)) {
      setTags([...tags, trimmed]);
    }
    setNewTag('');
  }

  const titleNode = (
    <div className="flex items-start gap-3">
      {display != null && <ScopeTile {...display.tile} size={44} />}
      <div className="min-w-0 flex-1">
        <span className="block font-mono text-[10.5px] font-medium uppercase tracking-[0.06em] text-muted-foreground">
          Knowledge entry{entry != null ? ` · ${entry.scope}` : ''}
        </span>
        <p className="mt-0.5 font-mono text-[11px] font-normal text-muted-foreground">
          {entry != null ? (
            <>
              {entry.topic} · v{entry.version}
              {entry.seededFrom != null && entry.lastEditorKind === 'human' && (
                <span data-testid="editor-seeded-banner"> · seeded from {entry.seededFrom}</span>
              )}
            </>
          ) : (
            'Fetching entry…'
          )}
        </p>
      </div>
    </div>
  );

  const footerNode = canEdit ? (
    <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
      <Button
        variant="ghost"
        onClick={() => setConfirmDelete(true)}
        data-testid="entry-delete"
        className="h-12 w-full text-destructive hover:text-destructive sm:h-auto sm:w-auto"
      >
        Delete entry
      </Button>
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button
          variant="ghost"
          onClick={onClose}
          className="h-12 w-full bg-muted/50 sm:h-auto sm:w-auto sm:bg-transparent"
        >
          Cancel
        </Button>
        <Button
          onClick={onSave}
          disabled={updateMutation.isPending}
          data-testid="entry-save"
          className="h-12 w-full sm:h-auto sm:w-auto"
        >
          {updateMutation.isPending ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </div>
  ) : undefined;

  return (
    <GLDrawer
      open={entryId != null}
      onOpenChange={(open) => !open && onClose()}
      variant="panel"
      mobileDragResetKey={entryId}
      defaultWidth={typeof window !== 'undefined' ? Math.round(window.innerWidth * 0.75) : 720}
      minWidth={360}
      storageKey="gl-knowledge-entry-panel-width"
      title={titleNode}
      srDescription={entry?.title ?? 'Knowledge entry'}
      mobileClassName="max-h-[92dvh] rounded-t-[20px]"
      contentProps={{ 'data-testid': 'knowledge-entry-editor' }}
      footer={footerNode}
    >
      <Tabs value={tab} onValueChange={(v) => setTab(v as 'edit' | 'history')}>
        <TabsList>
          <TabsTrigger value="edit">Edit</TabsTrigger>
          <TabsTrigger value="history" data-testid="entry-history-tab">
            History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="edit" className="mt-3 space-y-4">
          {!canEdit && entry != null && (
            <Alert data-testid="entry-readonly">
              <AlertTitle>Read-only</AlertTitle>
              <AlertDescription>Your role can read this entry but not edit it.</AlertDescription>
            </Alert>
          )}

          {/* Borderless title input */}
          <input
            aria-label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={!canEdit}
            className="w-full border-0 border-b border-transparent bg-transparent px-0 text-[23px] font-bold tracking-tight focus:border-border focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:opacity-100"
          />

          {/* Details strip */}
          <div className="flex flex-wrap items-start gap-x-10 gap-y-4 border-y border-border py-4">
            {/* Group 1: Agent visibility */}
            <div>
              <p className="mb-1.5 font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
                Agent visibility
              </p>
              <div
                role="group"
                aria-label="Agent visibility"
                className="inline-flex items-center gap-0.5"
                data-testid="entry-visibility"
              >
                {VISIBILITY_OPTIONS.map(({ v, label }) => (
                  <button
                    key={v}
                    type="button"
                    disabled={!canEdit}
                    aria-pressed={visibility === v}
                    onClick={() => setVisibility(v)}
                    className={cn(
                      'h-11 md:h-7 rounded-gl border border-transparent px-3 text-[12px] font-medium transition-colors',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
                      visibility === v
                        ? 'bg-foreground text-background'
                        : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Group 2: Tags */}
            <div>
              <p className="mb-1.5 font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
                Tags
              </p>
              <div className="flex flex-wrap items-center gap-1.5">
                {tags.map((tag) => (
                  <Tag key={tag} className={canEdit ? 'pr-0.5' : undefined}>
                    {tag}
                    {canEdit && (
                      <button
                        type="button"
                        aria-label={`Remove ${tag}`}
                        onClick={() => setTags(tags.filter((t) => t !== tag))}
                        className="inline-flex size-6 items-center justify-center rounded-full leading-none text-muted-foreground hover:bg-muted hover:text-foreground"
                      >
                        ×
                      </button>
                    )}
                  </Tag>
                ))}
                {canEdit && (
                  <input
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyDown={handleTagKeyDown}
                    placeholder="+ Tag"
                    maxLength={32}
                    data-testid="entry-tag-input"
                    className="rounded-full border border-dashed border-border bg-transparent px-2 py-0.5 text-[11px] placeholder:text-slate focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                  />
                )}
              </div>
            </div>
          </div>

          {/* Body card */}
          <div className="overflow-hidden rounded-gl border border-border">
            {/* Toolbar bar */}
            <div className="flex h-9 items-center justify-between border-b border-border bg-muted/50 px-3">
              <div className="inline-flex items-center gap-0.5">
                {canEdit ? (
                  (['write', 'preview'] as const).map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      aria-pressed={bodyMode === mode}
                      onClick={() => setBodyMode(mode)}
                      data-testid={`entry-body-${mode}`}
                      className={cn(
                        'h-6 rounded-gl px-2.5 text-[11px] font-medium capitalize transition-colors',
                        bodyMode === mode
                          ? 'bg-foreground text-background'
                          : 'text-muted-foreground hover:text-foreground',
                      )}
                    >
                      {mode}
                    </button>
                  ))
                ) : (
                  <span className="inline-flex h-6 items-center rounded-gl px-2.5 text-[11px] font-medium text-muted-foreground">
                    Preview
                  </span>
                )}
              </div>
              <span className="font-mono text-[11px] tabular-nums text-muted-foreground">
                {wordCount} words
              </span>
            </div>
            {effectiveBodyMode === 'write' ? (
              <Textarea
                aria-label="Body (Markdown)"
                value={bodyMd}
                onChange={(e) => setBodyMd(e.target.value)}
                rows={14}
                className="rounded-none border-0 font-mono text-xs focus-visible:ring-0"
              />
            ) : (
              <div
                data-testid="entry-body-preview-content"
                className="min-h-[220px] overflow-y-auto px-3 py-2"
              >
                {trimmedBody ? (
                  <MarkdownView
                    body={bodyMd}
                    assets={previewAssets}
                    assetsUnavailable={assetsUnavailable}
                    data-testid="entry-preview"
                  />
                ) : (
                  <p className="text-sm text-muted-foreground">Nothing to preview yet.</p>
                )}
              </div>
            )}
          </div>

          {entry != null && <KnowledgeAssets entryId={entry.id} canEdit={canEdit} />}
        </TabsContent>

        <TabsContent value="history" className="mt-3">
          {history.length === 0 ? (
            <p className="text-sm text-muted-foreground">No edits recorded yet.</p>
          ) : (
            <ul data-testid="entry-history-list">
              {history.map((h, idx) => (
                <li key={h.id} className="relative pb-4 pl-6 last:pb-0">
                  {/* Connector rail — hidden on the last item */}
                  {idx < history.length - 1 && (
                    <span
                      aria-hidden
                      className="absolute bottom-0 left-[5px] top-2 w-px bg-border"
                    />
                  )}
                  {/* Dot */}
                  <span
                    aria-hidden
                    className={cn(
                      'absolute left-0 top-1.5 h-2.5 w-2.5 rounded-full',
                      h.version === entry?.version
                        ? 'bg-[hsl(var(--link))]'
                        : 'border-2 border-[hsl(var(--link))] bg-background',
                    )}
                  />
                  {/* Header row */}
                  <div className="flex items-center gap-1.5">
                    <span className="text-[13px] font-semibold">v{h.version}</span>
                    {h.actorKind === 'agent' ? (
                      <span className="rounded bg-glow px-1.5 py-0.5 font-mono text-[9.5px] uppercase tracking-[0.06em] text-foreground">
                        Agent
                      </span>
                    ) : (
                      <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[9.5px] uppercase tracking-[0.06em] text-muted-foreground">
                        Human
                      </span>
                    )}
                  </div>
                  <p className="text-[13px]">{h.diffSummary}</p>
                  <p className="font-mono text-[11px] text-muted-foreground">
                    {h.action} · {formatKnowledgeDate(h.createdAt)}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </TabsContent>
      </Tabs>

      {/* Radix Dialog portals to <body>, so mounting the delete confirmation as a
          child of the drawer keeps it on top without re-nesting the editor tree. */}
      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent className="sm:max-w-md" data-testid="entry-delete-confirm">
          <DialogHeader>
            <DialogTitle>Delete entry</DialogTitle>
            <DialogDescription>
              {entry != null ? (
                <>
                  Permanently delete <span className="font-medium">{entry.title}</span> (
                  <code>{entry.topic}</code>). Agents will no longer read it. The deletion is
                  recorded in the audit log, but the entry can&apos;t be restored.
                </>
              ) : (
                ''
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setConfirmDelete(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => void onDelete()}
              data-testid="entry-delete-confirm-btn"
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete entry'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </GLDrawer>
  );
}
